import { Settings, Info, LayoutGrid } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { Divider } from '../../../components/ui/Divider';
import { PageHeader } from '../../../components/ui/PageHeader';
import { BackLink } from '../../../components/ui/BackLink';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { StatCard } from '../../../components/ui/StatCard';
import { CodeSnippet } from '../components/CodeSnippet';
import { Subsection } from '../components/Subsection';

// ============================================================================
// Sekcja: Card + StatCard
// ============================================================================

export function CardSection() {
  return (
    <div className="space-y-10">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Card>'}</code> -
        powierzchnia dla grupy treści. Warianty: default, interactive (hover glow),
        elevated (shadow-glass). Opcjonalne sloty header/footer.{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<StatCard>'}</code>{' '}
        jest specjalizowany dla dashboardów (tytuł + liczba + delta trend + ikona).
      </p>

      <Subsection n={1} title="Warianty">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card variant="default">
            <h4 className="text-sm font-semibold text-text-primary mb-1">Default</h4>
            <p className="text-xs text-text-muted">Najczęstszy wariant - bg-surface-raised/40 z subtelnym borderem.</p>
          </Card>
          <Card variant="interactive" onClick={() => {}}>
            <h4 className="text-sm font-semibold text-text-primary mb-1">Interactive</h4>
            <p className="text-xs text-text-muted">Hover glow + cursor pointer. Dla klikalnych kart (lista).</p>
          </Card>
          <Card variant="elevated">
            <h4 className="text-sm font-semibold text-text-primary mb-1">Elevated</h4>
            <p className="text-xs text-text-muted">Wyższe tło + shadow-glass. Dla eksponowanych/focal point.</p>
          </Card>
        </div>
      </Subsection>

      <Subsection n={2} title="Z header + footer">
        <Card
          variant="elevated"
          header={
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-text-primary">Zamówienie #12345</h4>
              <Badge variant="success" size="sm">Opłacone</Badge>
            </div>
          }
          footer={
            <div className="flex items-center justify-between gap-2">
              <Button variant="primary" size="sm">Zapisz</Button>
              <Button variant="ghost" size="sm">Anuluj</Button>
            </div>
          }
        >
          <p className="text-sm text-text-secondary">
            Treść środkowa karty. Header i footer mają automatyczne bordery i padding zgodny z
            padding kart.
          </p>
        </Card>
      </Subsection>

      <Subsection n={3} title="Padding variants">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {(['none', 'sm', 'md', 'lg'] as const).map(p => (
            <Card key={p} padding={p}>
              <div className={p === 'none' ? 'bg-accent-primary/10 p-2' : ''}>
                <code className="text-xs font-mono text-text-primary">padding="{p}"</code>
                {p === 'none' && <p className="text-[10px] text-text-muted mt-1">slot bez paddingu</p>}
              </div>
            </Card>
          ))}
        </div>
      </Subsection>

      <Subsection n={4} title="StatCard — dashboard KPI" description="Emoji w tle (subtle float + pulse animation), foreground emoji tile, animated gradient top bar, hover zwiększa opacity tła. Wzór z FinanceDashboardPage.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            emoji="💰"
            label="Przychód (brutto)"
            value="1 819,69 zł"
            accentColors={['#10b981', '#4ade80']}
          />
          <StatCard
            emoji="📊"
            label="Dochód (zysk brutto)"
            value="487,82 zł"
            accentColors={['#3b82f6', '#22d3ee']}
          />
          <StatCard
            emoji="💎"
            label="Zysk netto"
            value="443,92 zł"
            valueColor="text-success"
            accentColors={['#a855f7', '#8b5cf6']}
          />
          <StatCard
            emoji="🛒"
            label="Zamówienia"
            value="22"
            accentColors={['#06b6d4', '#2dd4bf']}
          />
          <StatCard
            emoji="📦"
            label="Sprzedane produkty"
            value="40"
            accentColors={['#f97316', '#fbbf24']}
          />
          <StatCard
            emoji="🧺"
            label="Średni koszyk"
            value="82,71 zł"
            accentColors={['#ec4899', '#fb7185']}
          />
          <StatCard
            emoji="📣"
            label="Wydatki na reklamę"
            value="637,26 zł"
            accentColors={['#ef4444', '#fb923c']}
          />
          <StatCard
            emoji="👆"
            label="CTR (Unikatowy CTR)"
            value="1.80%"
            accentColors={['#eab308', '#a3e635']}
          />
          <StatCard
            emoji="🎯"
            label="ROAS"
            value="2.9x"
            accentColors={['#6366f1', '#60a5fa']}
            delta={{ value: '+0.3x vs poprzedni mies.', trend: 'up' }}
          />
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Divider
// ============================================================================

export function DividerSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Divider>'}</code> -
        cienka linia separatora. Horizontal opcjonalnie z labelem w środku. Vertical dla inline
        grup (np. toolbar).
      </p>

      <Subsection n={1} title="Horizontal">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-6 space-y-6">
          <p className="text-sm text-text-secondary">Sekcja 1 - ustawienia ogólne</p>
          <Divider />
          <p className="text-sm text-text-secondary">Sekcja 2 - integracje</p>
          <Divider label="Opcjonalne" />
          <p className="text-sm text-text-secondary">Sekcja 3 - zaawansowane</p>
        </div>
      </Subsection>

      <Subsection n={2} title="Vertical (inline)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-6">
          <div className="flex items-center gap-3 text-sm text-text-secondary">
            <span>Admin</span>
            <Divider orientation="vertical" />
            <span>Ustawienia</span>
            <Divider orientation="vertical" />
            <span>Profil</span>
          </div>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: PageHeader
// ============================================================================

export function PageHeaderSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<PageHeader>'}</code>{' '}
        — standardowy nagłówek strony (Panel Finansowy, Laboratorium, Ustawienia itd.).
        Props: <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">title</code>,{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">accentWord</code>,{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">subtitle</code>,{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">icon</code>,{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">actions</code>.
      </p>

      <Subsection n={1} title="Standard (kanoniczny wzorzec)" description="Ikona + tytuł + subtitle. Jedyny obsługiwany wariant — stosujemy wszędzie gdzie strona ma header.">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-6">
          <PageHeader
            icon={Settings}
            title="Panel administratora"
            subtitle="Zarządzaj globalnymi ustawieniami aplikacji"
          />
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: BackLink
// ============================================================================

export function BackLinkSection() {
  return (
    <div className="space-y-6">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<BackLink>'}</code>{' '}
        — link powrotu na stronę rodzica. Umieszczaj nad{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<PageHeader>'}</code>.
        Wymaga React Router — lub podaj <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">onClick</code>{' '}
        zamiast <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">to</code>.
      </p>
      <Subsection n={1} title="Warianty">
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-surface-raised/40 p-6">
            <BackLink to="/administrator" label="Wróć do panelu administratora" />
            <PageHeader
              icon={Settings}
              title="Ustawienia szczegółowe"
              subtitle="Edycja szczegółowych parametrów"
            />
          </div>
          <div className="rounded-xl border border-border bg-surface-raised/40 p-6">
            <BackLink onClick={() => alert('navigate(-1) albo history.back()')} label="Wróć do listy" />
            <h2 className="text-xl font-semibold text-text-primary">Z onClick handlerem</h2>
          </div>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: SettingsSection
// ============================================================================

export function SettingsSectionDemo() {
  return (
    <div className="space-y-6">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<SettingsSection>'}</code>{' '}
        — karta sekcji ustawień. Zastępuje ręczne{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">rounded-xl bg-surface-raised/40 border p-6</code>{' '}
        wzorce. Opcje: <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">dense</code>,{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">flushContent</code> (dla listy ToggleRow).
      </p>

      <CodeSnippet
        language="tsx"
        code={`// Anatomia strony ustawień
<BackLink to="/parent" label="Wróć" />                      {/* opcjonalne */}
<PageHeader icon={Settings} title="Ustawienia X" subtitle="..." />

<div className="space-y-6">
  <SettingsSection icon={LayoutGrid} title="Układ" subtitle="...">
    <FormField label="Szerokość (px)" helper="Zalecane 1200-1440">
      <Input type="number" defaultValue="1200" />
    </FormField>
  </SettingsSection>

  <SettingsSection icon={Bell} title="Powiadomienia" flushContent>
    <ToggleRow label="..." description="..." checked=... onChange=... />
    <ToggleRow label="..." description="..." checked=... onChange=... />
  </SettingsSection>

  <SettingsSection icon={Sliders} title="Responsywność" dense>
    <BreakpointSlider value={padding} onChange={setPadding} unit="px" />
  </SettingsSection>
</div>

{/* Sticky save bar na końcu */}`}
      />

      <Subsection n={1} title="Standard">
        <SettingsSection
          icon={LayoutGrid}
          title="Układ i kontener"
          subtitle="Szerokość kontenera, wcięcia i domyślny odstęp między kolumnami"
        >
          <FormField label="Maksymalna szerokość kontenera (px)" htmlFor="dem-w" helper="Zalecane: 1200-1440px">
            <Input id="dem-w" type="number" defaultValue="1200" />
          </FormField>
        </SettingsSection>
      </Subsection>

      <Subsection n={2} title="Dense variant">
        <SettingsSection
          icon={Info}
          title="Dense variant"
          subtitle="Mniejszy padding dla zagnieżdżonych sekcji"
          dense
          actions={<Badge variant="info" size="sm">Nowe</Badge>}
        >
          <p className="text-sm text-text-secondary">Zawartość dense.</p>
        </SettingsSection>
      </Subsection>
    </div>
  );
}
